import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, MoreVertical, Pencil, Trash2, Clock, CalendarDays, Coffee } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const DAYS_OF_WEEK = [
  { value: 0, label: "Lunedì" },
  { value: 1, label: "Martedì" },
  { value: 2, label: "Mercoledì" },
  { value: 3, label: "Giovedì" },
  { value: 4, label: "Venerdì" },
  { value: 5, label: "Sabato" },
  { value: 6, label: "Domenica" },
];

const Calendars = () => {
  const { user, token } = useAuth();
  const [business, setBusiness] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [services, setServices] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCalendar, setEditingCalendar] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    staff_id: "",
    service_ids: [],
    slot_duration_minutes: 30,
    buffer_before_minutes: 0,
    buffer_after_minutes: 0,
    max_days_advance: 30,
    is_active: true,
    default_availability: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const bizResponse = await axios.get(`${API}/businesses`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      if (bizResponse.data.length > 0) {
        const biz = bizResponse.data[0];
        setBusiness(biz);

        const [calendarsResponse, servicesResponse, staffResponse] = await Promise.all([
          axios.get(`${API}/businesses/${biz.business_id}/calendars`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          }),
          axios.get(`${API}/businesses/${biz.business_id}/services`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          }),
          axios.get(`${API}/businesses/${biz.business_id}/staff`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          })
        ]);
        
        setCalendars(calendarsResponse.data);
        setServices(servicesResponse.data);
        setStaffList(staffResponse.data);
      }
    } catch (error) {
      toast.error("Errore nel caricamento dei calendari");
    } finally {
      setLoading(false);
    }
  };

  // New format with multiple periods per day (for breaks)
  const getDefaultAvailability = () => [
    { day_of_week: 0, is_available: true, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
    { day_of_week: 1, is_available: true, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
    { day_of_week: 2, is_available: true, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
    { day_of_week: 3, is_available: true, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
    { day_of_week: 4, is_available: true, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
    { day_of_week: 5, is_available: false, periods: [{ start_time: "09:00", end_time: "13:00" }] },
    { day_of_week: 6, is_available: false, periods: [] },
  ];

  // Convert old format to new format if needed
  const normalizeAvailability = (availability) => {
    if (!availability || availability.length === 0) {
      return getDefaultAvailability();
    }
    
    return availability.map(day => {
      // Already in new format
      if (day.periods && Array.isArray(day.periods)) {
        return day;
      }
      // Convert old format
      if (day.start_time && day.end_time) {
        return {
          day_of_week: day.day_of_week,
          is_available: day.is_available !== false,
          periods: [{ start_time: day.start_time, end_time: day.end_time }]
        };
      }
      return {
        day_of_week: day.day_of_week,
        is_available: false,
        periods: []
      };
    });
  };

  const openCreateDialog = () => {
    setEditingCalendar(null);
    setFormData({
      name: "",
      description: "",
      staff_id: "",
      service_ids: [],
      slot_duration_minutes: 30,
      buffer_before_minutes: 0,
      buffer_after_minutes: 0,
      max_days_advance: 30,
      is_active: true,
      default_availability: getDefaultAvailability()
    });
    setDialogOpen(true);
  };

  const openEditDialog = (calendar) => {
    setEditingCalendar(calendar);
    setFormData({
      name: calendar.name,
      description: calendar.description || "",
      staff_id: calendar.staff_id || "",
      service_ids: calendar.service_ids || [],
      slot_duration_minutes: calendar.slot_duration_minutes,
      buffer_before_minutes: calendar.buffer_before_minutes,
      buffer_after_minutes: calendar.buffer_after_minutes,
      max_days_advance: calendar.max_days_advance,
      is_active: calendar.is_active,
      default_availability: normalizeAvailability(calendar.default_availability)
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        staff_id: formData.staff_id || null
      };

      if (editingCalendar) {
        await axios.put(`${API}/calendars/${editingCalendar.calendar_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        toast.success("Calendario aggiornato!");
      } else {
        await axios.post(`${API}/businesses/${business.business_id}/calendars`, payload, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        toast.success("Calendario creato!");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (calendarId) => {
    if (!confirm("Sei sicuro di voler eliminare questo calendario?")) return;

    try {
      await axios.delete(`${API}/calendars/${calendarId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Calendario eliminato!");
      fetchData();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const toggleService = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      service_ids: prev.service_ids.includes(serviceId)
        ? prev.service_ids.filter(id => id !== serviceId)
        : [...prev.service_ids, serviceId]
    }));
  };

  const updateDayAvailability = (dayIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      default_availability: prev.default_availability.map((day, idx) =>
        idx === dayIndex ? { ...day, [field]: value } : day
      )
    }));
  };

  const updatePeriod = (dayIndex, periodIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      default_availability: prev.default_availability.map((day, idx) => {
        if (idx !== dayIndex) return day;
        const newPeriods = [...day.periods];
        newPeriods[periodIndex] = { ...newPeriods[periodIndex], [field]: value };
        return { ...day, periods: newPeriods };
      })
    }));
  };

  const addPeriod = (dayIndex) => {
    setFormData(prev => ({
      ...prev,
      default_availability: prev.default_availability.map((day, idx) => {
        if (idx !== dayIndex) return day;
        return {
          ...day,
          periods: [...day.periods, { start_time: "14:00", end_time: "18:00" }]
        };
      })
    }));
  };

  const removePeriod = (dayIndex, periodIndex) => {
    setFormData(prev => ({
      ...prev,
      default_availability: prev.default_availability.map((day, idx) => {
        if (idx !== dayIndex) return day;
        const newPeriods = day.periods.filter((_, pIdx) => pIdx !== periodIndex);
        return { ...day, periods: newPeriods };
      })
    }));
  };

  const getServiceName = (serviceId) => {
    return services.find(s => s.service_id === serviceId)?.name || serviceId;
  };

  const getStaffName = (staffId) => {
    return staffList.find(s => s.staff_id === staffId)?.name || "";
  };

  const formatPeriods = (periods) => {
    if (!periods || periods.length === 0) return "Chiuso";
    return periods.map(p => `${p.start_time}-${p.end_time}`).join(", ");
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!business) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Crea prima la tua attività dalla Dashboard</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="calendars-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Calendari
            </h1>
            <p className="text-slate-500 mt-1">Gestisci i calendari e le disponibilità con orari e pause</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreateDialog} data-testid="add-calendar-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuovo Calendario
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {editingCalendar ? "Modifica Calendario" : "Nuovo Calendario"}
                </DialogTitle>
                <DialogDescription>
                  {editingCalendar ? "Modifica le impostazioni del calendario" : "Crea un nuovo calendario con orari e pause"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome Calendario *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Es. Calendario Principale"
                      required
                      data-testid="calendar-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Descrizione</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Descrivi il calendario..."
                      rows={2}
                      data-testid="calendar-description-input"
                    />
                  </div>

                  {staffList.length > 0 && (
                    <div className="space-y-2">
                      <Label>Staff Assegnato</Label>
                      <Select
                        value={formData.staff_id || "none"}
                        onValueChange={(value) => setFormData({ ...formData, staff_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger data-testid="calendar-staff-select">
                          <SelectValue placeholder="Seleziona staff (opzionale)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nessuno</SelectItem>
                          {staffList.map((staff) => (
                            <SelectItem key={staff.staff_id} value={staff.staff_id}>
                              {staff.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Services */}
                {services.length > 0 && (
                  <div className="space-y-2">
                    <Label>Servizi Disponibili</Label>
                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border rounded-lg p-3">
                      {services.map((service) => (
                        <div key={service.service_id} className="flex items-center gap-2">
                          <Checkbox
                            id={`cal-service-${service.service_id}`}
                            checked={formData.service_ids.includes(service.service_id)}
                            onCheckedChange={() => toggleService(service.service_id)}
                            data-testid={`calendar-service-${service.service_id}`}
                          />
                          <label htmlFor={`cal-service-${service.service_id}`} className="text-sm cursor-pointer">
                            {service.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Slot Settings */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Durata Slot (min)</Label>
                    <Input
                      type="number"
                      min="5"
                      value={formData.slot_duration_minutes}
                      onChange={(e) => setFormData({ ...formData, slot_duration_minutes: parseInt(e.target.value) })}
                      data-testid="calendar-slot-duration"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Buffer Prima (min)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.buffer_before_minutes}
                      onChange={(e) => setFormData({ ...formData, buffer_before_minutes: parseInt(e.target.value) })}
                      data-testid="calendar-buffer-before"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Buffer Dopo (min)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.buffer_after_minutes}
                      onChange={(e) => setFormData({ ...formData, buffer_after_minutes: parseInt(e.target.value) })}
                      data-testid="calendar-buffer-after"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Giorni Anticipo</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.max_days_advance}
                      onChange={(e) => setFormData({ ...formData, max_days_advance: parseInt(e.target.value) })}
                      data-testid="calendar-max-days"
                    />
                  </div>
                </div>

                {/* Availability with Breaks */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Label>Orari di Apertura</Label>
                    <span className="text-xs text-slate-500">(puoi aggiungere pause pranzo)</span>
                  </div>
                  <div className="space-y-3">
                    {formData.default_availability.map((day, dayIndex) => (
                      <div key={dayIndex} className="border border-slate-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={day.is_available}
                              onCheckedChange={(checked) => updateDayAvailability(dayIndex, 'is_available', checked)}
                              data-testid={`avail-day-${dayIndex}`}
                            />
                            <span className="font-medium text-sm">{DAYS_OF_WEEK[dayIndex]?.label}</span>
                          </div>
                          {day.is_available && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => addPeriod(dayIndex)}
                              data-testid={`add-period-${dayIndex}`}
                            >
                              <Coffee className="h-3 w-3 mr-1" />
                              Aggiungi Fascia
                            </Button>
                          )}
                        </div>
                        
                        {day.is_available && (
                          <div className="space-y-2 ml-7">
                            {day.periods.map((period, periodIndex) => (
                              <div key={periodIndex} className="flex items-center gap-2">
                                <Input
                                  type="time"
                                  value={period.start_time}
                                  onChange={(e) => updatePeriod(dayIndex, periodIndex, 'start_time', e.target.value)}
                                  className="w-28"
                                  data-testid={`period-start-${dayIndex}-${periodIndex}`}
                                />
                                <span className="text-slate-400">-</span>
                                <Input
                                  type="time"
                                  value={period.end_time}
                                  onChange={(e) => updatePeriod(dayIndex, periodIndex, 'end_time', e.target.value)}
                                  className="w-28"
                                  data-testid={`period-end-${dayIndex}-${periodIndex}`}
                                />
                                {day.periods.length > 1 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removePeriod(dayIndex, periodIndex)}
                                    className="text-red-500 hover:text-red-700"
                                    data-testid={`remove-period-${dayIndex}-${periodIndex}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            ))}
                            {day.periods.length === 0 && (
                              <p className="text-xs text-slate-400">Nessuna fascia oraria configurata</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="active">Calendario Attivo</Label>
                  <Switch
                    id="active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    data-testid="calendar-active-switch"
                  />
                </div>

                <DialogFooter>
                  <Button type="submit" disabled={saving} data-testid="save-calendar-btn">
                    {saving ? "Salvataggio..." : editingCalendar ? "Salva Modifiche" : "Crea Calendario"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Calendars Grid */}
        {calendars.length === 0 ? (
          <Card className="border-dashed border-2 border-slate-200">
            <CardContent className="py-12 text-center">
              <p className="text-slate-500 mb-4">Nessun calendario ancora creato</p>
              <Button onClick={openCreateDialog} data-testid="empty-add-calendar-btn">
                <Plus className="h-4 w-4 mr-2" />
                Crea il tuo primo calendario
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
            {calendars.map((calendar) => {
              const normalizedAvail = normalizeAvailability(calendar.default_availability);
              return (
                <Card key={calendar.calendar_id} className="border-slate-100 shadow-sm card-hover" data-testid={`calendar-card-${calendar.calendar_id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {calendar.name}
                        </CardTitle>
                        {!calendar.is_active && (
                          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                            Disattivato
                          </span>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`calendar-menu-${calendar.calendar_id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(calendar)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Modifica
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleDelete(calendar.calendar_id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Elimina
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {calendar.description && (
                      <p className="text-sm text-slate-500 mb-4 line-clamp-2">{calendar.description}</p>
                    )}
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Clock className="h-4 w-4" />
                        <span>Slot: {calendar.slot_duration_minutes} min</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <CalendarDays className="h-4 w-4" />
                        <span>Anticipo max: {calendar.max_days_advance} giorni</span>
                      </div>
                      {calendar.staff_id && (
                        <div className="text-slate-600">
                          Staff: {getStaffName(calendar.staff_id)}
                        </div>
                      )}
                    </div>

                    {/* Show availability summary */}
                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <p className="text-xs text-slate-500 mb-2">Orari:</p>
                      <div className="space-y-1">
                        {normalizedAvail.slice(0, 3).map((day, idx) => (
                          <div key={idx} className="flex justify-between text-xs">
                            <span className="text-slate-600">{DAYS_OF_WEEK[idx]?.label?.slice(0, 3)}</span>
                            <span className={day.is_available ? "text-slate-900" : "text-slate-400"}>
                              {day.is_available ? formatPeriods(day.periods) : "Chiuso"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {calendar.service_ids?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs text-slate-500 mb-2">Servizi:</p>
                        <div className="flex flex-wrap gap-1">
                          {calendar.service_ids.slice(0, 3).map((serviceId) => (
                            <span key={serviceId} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                              {getServiceName(serviceId)}
                            </span>
                          ))}
                          {calendar.service_ids.length > 3 && (
                            <span className="text-xs text-slate-500">+{calendar.service_ids.length - 3}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Calendars;
